import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText } from '../copy/text.js';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Alert from '@cloudscape-design/components/alert';
import type { UnifiedRecordsState } from '../state/unified-records-state.js';
import type { ExecuteRelationshipQuery } from '../state/unified-record-page.js';
import { exportRelationships } from '../presentation/export-relationships.js';
import { relationshipExportState } from '../state/relationship-export-state.js';

export function ExportDialog({
  store,
  execute,
}: {
  store: UnifiedRecordsState;
  execute: ExecuteRelationshipQuery;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'raw' | 'decoded'>('decoded');
  const [scope, setScope] = useState<'filtered' | 'selected'>('filtered');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const cancelled = useRef(false);
  useEffect(
    () => () => {
      cancelled.current = true;
    },
    [],
  );
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  async function download(): Promise<void> {
    cancelled.current = false;
    setBusy(true);
    setStatus('');
    setError('');
    const snapshot = store.getSnapshot();
    const fields = store.fields.getSnapshot();
    try {
      const { query, columns } = relationshipExportState(store, scope === 'selected');
      const result = await exportRelationships(execute, {
        query,
        fields: columns,
        mode,
        timezone: fields.preference.timezone,
        cancelled: () => cancelled.current,
        current: () =>
          store.getSnapshot().generation === snapshot.generation &&
          store.fields.getSnapshot() === fields,
        progress: (records, total) =>
          setStatus(
            message(viewerStrings.webComponentsExportDialog.associationRows, {
              value1: records.toLocaleString(),
              value2: total.toLocaleString(),
            }),
          ),
      });
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cdr-clarity-${scope}-${mode}.csv`;
      link.click();
      // Allow the browser to start consuming the download before releasing its Blob.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setStatus(
        message(
          viewerStrings.webComponentsExportDialog.rowsExportedCallsAndMediaObservationsPotentially,
          {
            value1: result.rows.toLocaleString(),
            value2: result.counts.primaryRecords.toLocaleString(),
            value3: result.counts.relatedRecords.toLocaleString(),
            value4: result.protectedCells.toLocaleString(),
          },
        ),
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {hasText(viewerStrings.webComponentsExportDialog.export) ? (
        <button type="button" onClick={() => setOpen(true)}>
          {viewerStrings.webComponentsExportDialog.export}
        </button>
      ) : null}
      <Modal
        visible={open}
        header={optionalText(viewerStrings.webComponentsExportDialog.exportRecords)}
        onDismiss={() => {
          cancelled.current = true;
          setOpen(false);
        }}
      >
        <div className="field-tools" data-copy-group>
          <select
            aria-label={optionalText(viewerStrings.webComponentsExportDialog.exportRecords2)}
            value={scope}
            disabled={busy}
            onChange={(event) =>
              setScope(event.target.value === 'selected' ? 'selected' : 'filtered')
            }
          >
            {hasText(viewerStrings.webComponentsExportDialog.filteredRecords) ? (
              <option value="filtered">
                {viewerStrings.webComponentsExportDialog.filteredRecords}
              </option>
            ) : null}
            {hasText(viewerStrings.webComponentsExportDialog.selectedRecords) ? (
              <option value="selected">
                {message(viewerStrings.webComponentsExportDialog.selectedRecords, {
                  value1: state.selected.size,
                })}
              </option>
            ) : null}
          </select>
          <select
            aria-label={optionalText(viewerStrings.webComponentsExportDialog.exportValues)}
            value={mode}
            disabled={busy}
            onChange={(event) => setMode(event.target.value === 'raw' ? 'raw' : 'decoded')}
          >
            {hasText(viewerStrings.webComponentsExportDialog.decodedValues) ? (
              <option value="decoded">
                {viewerStrings.webComponentsExportDialog.decodedValues}
              </option>
            ) : null}
            {hasText(viewerStrings.webComponentsExportDialog.rawValues) ? (
              <option value="raw">{viewerStrings.webComponentsExportDialog.rawValues}</option>
            ) : null}
          </select>
          {hasText(viewerStrings.webComponentsExportDialog.downloadCSV) ? (
            <button type="button" disabled={busy} onClick={() => void download()}>
              {viewerStrings.webComponentsExportDialog.downloadCSV}
            </button>
          ) : null}
          {busy &&
            (hasText(viewerStrings.webComponentsExportDialog.cancel) ? (
              <button
                type="button"
                onClick={() => {
                  cancelled.current = true;
                }}
              >
                {viewerStrings.webComponentsExportDialog.cancel}
              </button>
            ) : null)}
        </div>
        {hasText(status) && <p role="status">{status}</p>}
        {hasText(error) && <Alert type="error">{error}</Alert>}
      </Modal>
    </>
  );
}
